import { DotsMenu } from '@griply/assets';
import { getVideoCallMeetingCode } from '@griply/utils';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/ui/button';
import { Sizes, Variants } from 'shared/components/ui/button/types';
import {
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from '../event-form.style';
import { EventRow } from '../event-row';

export type EventVideoCallRowProps = {
  url: string;
  // the editable form carries the icon on the toggle row above this one, so it passes none
  icon?: React.FC;
  // composed by getCalendarEventVideoCall: the provider's own conference label, else the enum name
  label: string;
  // present only when swapping this conference for Meet is possible AND meaningful; omitted, the row is read-only
  onReplace?: () => void;
  // the swap is armed and happens on save; the link below is the one still on the event
  isReplacePending?: boolean;
  // omitted wherever the call cannot be removed — a read-only surface, or a locked Outlook meeting
  onDelete?: () => void;
};

export const EventVideoCallRow: React.FC<EventVideoCallRowProps> = ({
  url,
  icon,
  label,
  onReplace,
  isReplacePending,
  onDelete,
}) => {
  const { t } = useTranslation();

  const meetingCode = useMemo(() => getVideoCallMeetingCode(url), [url]);

  // non-zero while the confirmation is on screen, and its value keys the element
  const [copyCount, setCopyCount] = useState(0);

  const menuTriggerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  // clipboard access is denied on insecure origins and by permission, neither of which is a bug
  const copy = (text: string) => {
    closeMenu();
    navigator.clipboard?.writeText(text).catch(() => {});
    // a fresh key remounts the message, restarting the fade when copied again mid-animation
    setCopyCount((count) => count + 1);
  };

  const onCopyCode = () => meetingCode && copy(meetingCode);
  const onCopyLink = () => copy(url);

  const onSelectDelete = () => {
    closeMenu();
    onDelete?.();
  };

  // the fade running out is what clears it — no timer, so nothing to cancel on unmount
  const onCopiedFaded = () => setCopyCount(0);

  return (
    <EventRow icon={icon} alignTop>
      <Styled.VideoCallCompact>
        <Styled.VideoCallCompactLine>
          <Styled.VideoCallLink href={url} target="_blank" rel="noreferrer">
            {label}
            {!!meetingCode && (
              <Styled.VideoCallCode>{meetingCode}</Styled.VideoCallCode>
            )}
          </Styled.VideoCallLink>
          <Styled.VideoCallCopy ref={menuTriggerRef}>
            <IconButton
              icon={DotsMenu}
              size={IconButtonSizes.Small}
              onClick={openMenu}
              type="button"
              aria-label={t('context-menus.external-event.labels.more')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            />
            {menuOpen && (
              <PopupMenu
                referenceElement={menuTriggerRef}
                onOutside={closeMenu}
                position="bottom-end"
              >
                <PopupMenuList hasBottomBorder>
                  {!!meetingCode && (
                    <PopupMenuListItem>
                      <PopupMenuButton onClick={onCopyCode}>
                        {t('forms.event.fields.video-call.copy-code')}
                      </PopupMenuButton>
                    </PopupMenuListItem>
                  )}
                  <PopupMenuListItem>
                    <PopupMenuButton onClick={onCopyLink}>
                      {t('forms.event.fields.video-call.copy-link')}
                    </PopupMenuButton>
                  </PopupMenuListItem>
                </PopupMenuList>
                {!!onDelete && (
                  <PopupMenuList>
                    <PopupMenuListItem>
                      <PopupMenuButton
                        onClick={onSelectDelete}
                        textColor={Colors.Error}
                      >
                        {t('forms.event.fields.video-call.delete')}
                      </PopupMenuButton>
                    </PopupMenuListItem>
                  </PopupMenuList>
                )}
              </PopupMenu>
            )}
          </Styled.VideoCallCopy>
        </Styled.VideoCallCompactLine>

        {!!copyCount && (
          <Styled.VideoCallCopied
            key={copyCount}
            role="status"
            onAnimationEnd={onCopiedFaded}
          >
            {t('forms.event.fields.video-call.copied')}
          </Styled.VideoCallCopied>
        )}

        {isReplacePending && (
          <Styled.VideoCallNote>
            {t('forms.event.fields.video-call.replace-pending')}
          </Styled.VideoCallNote>
        )}

        {!!onReplace && !isReplacePending && (
          <Styled.VideoCallAction>
            <Button
              type="button"
              variant={Variants.Text}
              size={Sizes.Small}
              onClick={onReplace}
            >
              {t('forms.event.fields.video-call.replace')}
            </Button>
          </Styled.VideoCallAction>
        )}
      </Styled.VideoCallCompact>
    </EventRow>
  );
};
